import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/constants/query-keys';
import { useInfiniteList } from '@/hooks/useInfiniteList';

import {
  enrolMember,
  type EnrolSlot,
  getCheckIns,
  getCommand,
  getMemberAttendance,
  getPeakHours,
  getSummary,
  linkMachineUser,
  listDevices,
  listInactive,
  listMachineUsers,
  listPunches,
  type MachineUserFilter,
  type PunchListFilter,
  restartDevice,
} from '../api/attendance.api';

/** Punches arrive on their own, so the screen refreshes while it is open. */
const LIVE_REFRESH_MS = 30_000;
/** A command waiting on the machine is checked this often (it polls every 20 s). */
const COMMAND_REFRESH_MS = 2000;
const INACTIVE_PAGE_SIZE = 10;

export function usePunches(filter: PunchListFilter) {
  return useQuery({
    queryKey: QUERY_KEYS.attendance.punches({ ...filter }),
    queryFn: ({ signal }) => listPunches(filter, signal),
    placeholderData: keepPreviousData,
    refetchInterval: LIVE_REFRESH_MS,
  });
}

export function useCheckIns(date: string) {
  return useQuery({
    queryKey: QUERY_KEYS.attendance.checkIns(date),
    queryFn: ({ signal }) => getCheckIns(date, signal),
    placeholderData: keepPreviousData,
    refetchInterval: LIVE_REFRESH_MS,
  });
}

export function useAttendanceDevices(enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.attendance.devices(),
    queryFn: ({ signal }) => listDevices(signal),
    refetchInterval: LIVE_REFRESH_MS,
    enabled,
  });
}

export function useAttendanceSummary(date: string, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.attendance.summary(date),
    queryFn: ({ signal }) => getSummary(date, signal),
    placeholderData: keepPreviousData,
    refetchInterval: LIVE_REFRESH_MS,
    enabled,
  });
}

/** Active members not seen for `days` days, loaded as the list scrolls. */
export function useInactiveFeed(days: number) {
  return useInfiniteList({
    queryKey: QUERY_KEYS.attendance.inactive(days),
    fetchPage: (page, signal) => listInactive(days, { page, pageSize: INACTIVE_PAGE_SIZE }, signal),
  });
}

export function usePeakHours(from: string, to: string) {
  return useQuery({
    queryKey: QUERY_KEYS.attendance.peakHours(from, to),
    queryFn: ({ signal }) => getPeakHours(from, to, signal),
    placeholderData: keepPreviousData,
  });
}

export function useMachineUsers(filter: MachineUserFilter, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.attendance.users({ ...filter }),
    queryFn: ({ signal }) => listMachineUsers(filter, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useMemberAttendance(memberId: number, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.attendance.member(memberId),
    queryFn: ({ signal }) => getMemberAttendance(memberId, signal),
    enabled,
  });
}

/** Every attendance figure can change after a link or a machine action. */
function useAttendanceChanged() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.attendance.all });
}

export function useLinkMachineUser() {
  const onChanged = useAttendanceChanged();
  return useMutation({
    mutationFn: ({ userId, memberId }: { userId: number; memberId: number | null }) =>
      linkMachineUser(userId, memberId),
    onSuccess: onChanged,
  });
}

export function useEnrolMember(memberId: number) {
  const onChanged = useAttendanceChanged();
  return useMutation({
    mutationFn: (slot: EnrolSlot) => enrolMember(memberId, slot),
    onSuccess: onChanged,
  });
}

export function useRestartDevice() {
  return useMutation({ mutationFn: (deviceId: number) => restartDevice(deviceId) });
}

/** A queued machine command, checked until the machine has answered it. */
export function useCommandProgress(id: number | null) {
  return useQuery({
    queryKey: QUERY_KEYS.attendance.command(id ?? 0),
    queryFn: ({ signal }) => getCommand(id ?? 0, signal),
    enabled: id !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'done' || status === 'failed' ? false : COMMAND_REFRESH_MS;
    },
  });
}
